import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  VegaModal,
  VegaFlex,
  VegaInputSelect,
  VegaButton,
  VegaButtonLink,
  VegaAccordion,
  VegaBox,
  VegaInput,
  VegaButtonCircle,
  VegaRadio,
  VegaForm
} from '@heartlandone/vega-react';
// import { VegaLoader } from '@heartlandone/vega';
import PropTypes from 'prop-types';
import './UserProfile.scss';
import { getFromlocalStorage } from 'Chores/utils';
import { updateUserAllocationBulk } from 'Chores/request';

const EPSILON = 0.000001;

const getAllocationValue = (item) => Number(item?.allocation ?? 0);

const clampIndex = (index, length) => {
  if (typeof index !== 'number' || Number.isNaN(index)) {
    return null;
  }
  if (index < 0 || index >= length) {
    return null;
  }
  return index;
};

const computePrimaryControls = (items = [], currentSelection) => {
  const list = Array.isArray(items) ? items : [];
  const length = list.length;
  const disabled = Array.from({ length }, () => false);

  if (length === 0) {
    return { show: false, disabled, resolvedSelection: null };
  }

  if (length === 1) {
    return { show: false, disabled: [true], resolvedSelection: 0 };
  }

  const findFallbackIndex = () => {
    let resolved = clampIndex(currentSelection, length);
    if (resolved === null) {
      resolved = list.findIndex((item) => item?.isPrimary);
      if (resolved === -1) {
        resolved = 0;
      }
    }
    return resolved;
  };

  if (length > 2) {
    const allocations = list.map(getAllocationValue);
    const totalAllocation = allocations.reduce((acc, value) => acc + value, 0);
    const sumIsOne = Math.abs(totalAllocation - 1) < EPSILON;
    const maxAllocation = Math.max(...allocations);
    const minAllocation = Math.min(...allocations);
    const hasDifferentAllocations = Math.abs(maxAllocation - minAllocation) > EPSILON;

    if (sumIsOne && hasDifferentAllocations) {
      const allowedIndices = allocations.map((value) => Math.abs(value - maxAllocation) < EPSILON);
      const disabledStates = allowedIndices.map((allowed) => !allowed);

      let resolved = clampIndex(currentSelection, length);
      if (resolved === null || disabledStates[resolved]) {
        resolved = list.findIndex((item, idx) => item?.isPrimary && !disabledStates[idx]);
        if (resolved === -1) {
          resolved = allowedIndices.findIndex(Boolean);
        }
        if (resolved === -1) {
          resolved = null;
        }
      }

      return { show: true, disabled: disabledStates, resolvedSelection: resolved };
    }

    return { show: true, disabled, resolvedSelection: findFallbackIndex() };
  }

  const firstAllocation = getAllocationValue(list[0]);
  const secondAllocation = getAllocationValue(list[1]);
  const sum = firstAllocation + secondAllocation;
  const sumIsOne = Math.abs(sum - 1) < EPSILON;

  if (!sumIsOne) {
    return { show: true, disabled: [true, true], resolvedSelection: null };
  }

  const equalAllocations = Math.abs(firstAllocation - secondAllocation) < EPSILON;

  if (equalAllocations) {
    return { show: true, disabled, resolvedSelection: findFallbackIndex() };
  }

  const dominantIndex = firstAllocation > secondAllocation ? 0 : 1;
  const resolvedSelection = dominantIndex;
  const disabledStates = disabled.map((value, idx) => (idx === dominantIndex ? value : true));

  return { show: true, disabled: disabledStates, resolvedSelection };
};

const matchesAllocation = (item, targetId, targetIndex, currentIndex) => {
  if (targetId !== undefined && targetId !== null) {
    return item.id === targetId;
  }
  return currentIndex === targetIndex;
};

const allocationHasChanged = (baseline, current) => {
  if (!baseline) {
    return true;
  }
  if (!current) {
    return false;
  }

  const normalize = (value) => Number(value ?? 0);

  return (
    baseline.isPrimary !== current.isPrimary ||
    baseline.projectId !== current.projectId ||
    baseline.scrumTeamId !== current.scrumTeamId ||
    Math.abs(normalize(baseline.allocation) - normalize(current.allocation)) > EPSILON
  );
};

const UserProfileUpdateBusinessInformationModal = ({ open = false, onClose = () => {}, scrumTeams = [], userAllocations = [], projects, userId, loadUserInfo }) => {
  const [list, setList] = useState([]);
  const [basicList, setBasicList] = useState([]);
  const [comfrimModal, setComfrimModal] = useState(false);
  const [businessUnitNameList, setBusinessUnitNameList] = useState([]);
  const [marketcodeSource, setMarketcodeSource] = useState([]);
  const [primarySelectionIndex, setPrimarySelectionIndex] = useState(null);
  const businessInformationForm = useRef();
  const submitButtonRef = useRef(null);
  const primaryControls = useMemo(() => computePrimaryControls(list, primarySelectionIndex), [list, primarySelectionIndex]);
  const { show: showPrimaryAllocation, disabled: primaryDisabled, resolvedSelection } = primaryControls;
  const usedTeamIdSet = useMemo(() => {
    const ids = list.map((item) => item.scrumTeamId).filter(Boolean);
    return new Set(ids);
  }, [list]);

  const getAvailableTeamOptions = (item) => {
    const baseList = Array.isArray(item.teamList) && item.teamList.length > 0 ? item.teamList : scrumTeams;
    if (!Array.isArray(baseList)) {
      return [];
    }

    return baseList.filter((team) => {
      if (!team || !team.id) {
        return false;
      }
      if (item.scrumTeamId && item.scrumTeamId === team.id) {
        return true;
      }
      return !usedTeamIdSet.has(team.id);
    });
  };

  useEffect(() => {
    const allDictType = getFromlocalStorage('allDictType');
    const list = allDictType.find((i) => i.name === 'Business Unit Name').dictData;
    setBusinessUnitNameList(list?.map((i) => ({ id: i.id, displayName: i.name })));

    const marketCode = getFromlocalStorage('allDictType');
    const marketCodelist = marketCode.find((i) => i.name === 'Market Code').dictData;
    setMarketcodeSource(marketCodelist?.map((i) => ({ id: i.id, displayName: i.name })));
  }, []);

  useEffect(() => {
    if (open && userAllocations.length > 0 && projects.length > 0 && scrumTeams.length > 0) {
      const res = userAllocations.map((i) => {
        const allProjectList = projects.filter((p) => p.companyDictDataId === i.businessUnitId);
        const project = { id: i.projectId, displayName: i.projectName };
        const combined = [...allProjectList, project];
        let uniqueProjectList = Array.from(new Set(combined.map((p) => p.id))).map((id) => {
          return combined.find((p) => p.id === id);
        });
        uniqueProjectList = uniqueProjectList.sort((a, b) => a.displayName.localeCompare(b.displayName));
        return {
          id: i.id,
          businessUnitId: i.businessUnitId,
          isPrimary: i.isPrimary,
          projectId: i.projectId,
          projectList: uniqueProjectList,
          scrumTeamId: i.scrumTeamId,
          teamList: scrumTeams.filter((p) => p.projectId === i.projectId),
          marketCodeId: i.marketCode?.id,
          expand: true,
          allocation: i.allocation,
          userId: i.userId
        };
      });
      res.sort((a, b) => b.allocation - a.allocation);
      setList(res);
      setBasicList(res);
    }
  }, [open, userAllocations, projects, scrumTeams]);

  useEffect(() => {
    if (resolvedSelection !== primarySelectionIndex) {
      setPrimarySelectionIndex(resolvedSelection);
    }
  }, [resolvedSelection, primarySelectionIndex]);

  useEffect(() => {
    setList((prevList) => {
      if (!Array.isArray(prevList) || prevList.length === 0) {
        return prevList;
      }

      let changed = false;
      const nextList = prevList.map((item, index) => {
        const shouldBePrimary = primarySelectionIndex === index;
        if (shouldBePrimary) {
          return { ...item, isPrimary: true };
        }
        changed = true;
        return { ...item, isPrimary: false };
      });

      return changed ? nextList : prevList;
    });
  }, [primarySelectionIndex]);

  const createNewItem = () => {
    const newItem = {
      businessUnitId: '',
      isPrimary: false,
      projectId: '',
      scrumTeamId: '',
      marketCodeId: '',
      expand: true,
      allocation: 0,
      projectList: projects,
      teamList: scrumTeams
    };
    setList((prevList) => [...prevList, newItem]);
  };

  const onChangeProject = (projectId, userAllocationId, allocationIndex) => {
    if (!projectId) {
      return;
    }

    const project = projects.find((i) => i.id === projectId);
    if (!project) {
      return;
    }

    setList((prevList) => prevList.map((item, index) => {
      if (!matchesAllocation(item, userAllocationId, allocationIndex, index)) {
        return item;
      }

      if (item.projectId === projectId) {
        return item;
      }

      return {
        ...item,
        projectId,
        scrumTeamId: '',
        marketCodeId: project.marketCodeId,
        allocation: 0,
        businessUnitId: project.companyDictDataId,
        projectList: projects.filter((p) => p.companyDictDataId === project.companyDictDataId),
        teamList: scrumTeams.filter((p) => p.projectId === project.id)
      };
    }));
  };

  const onChangeTeam = (teamId, userAllocationId, allocationIndex) => {
    if (!teamId) {
      setList((prevList) => prevList.map((item, index) => {
        if (!matchesAllocation(item, userAllocationId, allocationIndex, index)) {
          return item;
        }

        if (!item.scrumTeamId) {
          return item;
        }

        return { ...item, scrumTeamId: '' };
      }));
      return;
    }

    const team = scrumTeams.find((i) => i.id === teamId);
    if (!team) {
      return;
    }
    const project = projects.find((i) => i.id === team.projectId);
    if (!project) {
      return;
    }
    setList((prevList) => prevList.map((item, index) => {
      if (!matchesAllocation(item, userAllocationId, allocationIndex, index)) {
        return item;
      }

      if (item.scrumTeamId === teamId) {
        return item;
      }

      return {
        ...item,
        projectId: team.projectId,
        scrumTeamId: teamId,
        marketCodeId: project.marketCodeId,
        allocation: 0,
        businessUnitId: project.companyDictDataId,
        projectList: projects.filter((p) => p.companyDictDataId === project.companyDictDataId),
        teamList: scrumTeams
      };
    }));
  };

  const onChangeBusinessUnit = (businessUnitId, userAllocationId, allocationIndex) => {
    if (!businessUnitId) {
      return;
    }
    const projectFilterLists = projects.filter((p) => p.companyDictDataId === businessUnitId).sort((a, b) => a.displayName.localeCompare(b.displayName));
    const projectIds = projectFilterLists.map((project) => project.id);
    setList((prevList) => prevList.map((item, index) => {
      if (!matchesAllocation(item, userAllocationId, allocationIndex, index)) {
        return item;
      }

      if (item.businessUnitId === businessUnitId) {
        return item;
      }

      return {
        ...item,
        projectId: '',
        scrumTeamId: '',
        marketCodeId: '',
        allocation: 0,
        businessUnitId,
        projectList: projectFilterLists,
        teamList: scrumTeams.filter((t) => projectIds.includes(t.projectId))
      };
    }));
  };

  const setPrimaryAllocation = (targetIndex, value) => {
    if (typeof targetIndex !== 'number' || Number.isNaN(targetIndex)) {
      return;
    }

    if (!Array.isArray(primaryDisabled) || targetIndex < 0 || targetIndex >= primaryDisabled.length) {
      return;
    }

    if (value === false) {
      return;
    }

    if (primaryDisabled[targetIndex]) {
      return;
    }

    setPrimarySelectionIndex(targetIndex);
  };

  const removeItem = (id, allocationIndex) => {
    setList((prevList) => prevList.filter((item, index) => !matchesAllocation(item, id, allocationIndex, index)));
  };

  const saveAllocations = async () => {
    try {
      const baselineMap = new Map(basicList.filter((baseline) => baseline?.id).map((baseline) => [baseline.id, baseline]));
      const resolvedUserId = userId || keycloak.idTokenParsed.sub;
      const upserts = [];

      list.forEach((item, index) => {
        const normalizedItem = { ...item, isPrimary: index === primarySelectionIndex };
        const payload = {
          id: item.id,
          userId: resolvedUserId,
          isPrimary: normalizedItem.isPrimary,
          projectId: item.projectId,
          projectName: projects.find((p) => p.id === item.projectId)?.displayName || '',
          scrumTeamId: item.scrumTeamId,
          allocation: item.allocation
        };

        if (!item.id) {
          upserts.push(payload);
          return;
        }

        const baseline = baselineMap.get(item.id);
        if (allocationHasChanged(baseline, normalizedItem)) {
          upserts.push(payload);
        }
      });

      const listIds = list.map((item) => item.id).filter(Boolean);
      const baselineIds = basicList.map((item) => item.id).filter(Boolean);
      const deleteTargets = baselineIds.filter((id) => !listIds.includes(id));

      if (upserts.length === 0 && deleteTargets.length === 0) {
        console.log('No allocation changes detected.');
        return true;
      }

      await updateUserAllocationBulk({
        upserts,
        deleteIds: deleteTargets
      });
      console.log('All allocations have been processed successfully.');
      return true;
    } catch (error) {
      console.error('Error processing allocations:', error);
    }
  };

  const save = async () => {
    businessInformationForm.current.valid().then(async (result) => {
      const isBulkDelete = list.length === 0 && basicList.length > 0;
      if (!isBulkDelete) {
        const allocationSum = list.reduce((acc, cur) => acc + Number(cur?.allocation ?? 0), 0);
        if (Math.abs(allocationSum - 1) > EPSILON) {
          VegaNotify.open({
            type: 'error',
            message: 'The sum of Shared Percentage must equal 1'
          });
          return;
        }

        if (list.some((item) => !item.businessUnitId || !item.projectId || !item.scrumTeamId)) {
          const err = new Error('Please complete all required fields in each allocation assignment.');
          VegaNotify.open({
            type: 'error',
            message: err.message
          });
          return;
        }

        if (list.length === 0) {
          return;
        }
      }
      // const loadId = VegaLoader.load({
      //   containerSelector: '#businessInformationModal .modal-dialog'
      // });
      submitButtonRef.current.disabled = true;
      const res = await saveAllocations();
      if (res) {
        VegaNotify.open({
          type: 'success',
          message: 'Business Information updated successfully.'
        });
        // VegaLoader.close(loadId);
        handleClose();
        loadUserInfo();
        submitButtonRef.current.disabled = false;
      }
    });
  };

  const PercentageRule = {
    canEvaluate(input, status) {
      return true;
    },
    evaluate: (input) => {
      const allocation = list.reduce((acc, cur) => acc + cur?.allocation ?? 0, 0).toFixed(2);
      if (input < 0) {
        return { isValid: false, message: 'No available allocation.' };
      }
      if (allocation > 1) {
        return { isValid: false, message: 'No available allocation.' };
      }
      if (allocation < 1) {
        return { isValid: false, message: 'No available allocation.' };
      }
      return { isValid: true, message: '' };
    }
  };

  const onChangePercentage = (value, userAllocationId, allocationIndex) => {
    setList((prevList) => prevList.map((item, index) => {
      if (!matchesAllocation(item, userAllocationId, allocationIndex, index)) {
        return item;
      }
      const allocation = Number(value) / 100;
      return { ...item, allocation };
    }));
  };

  const handleClose = () => {
    setList([]);
    setPrimarySelectionIndex(null);
    onClose();
  };

  const openComfrimModal = () => {
    setComfrimModal(true);
  };

  const closeComfrimModal = () => {
    setComfrimModal(false);
  };

  return (
    <>
      <VegaModal
        open={open}
        id={'businessInformationModal'}
        size={'800'}
        modalTitle='Edit Business Information'
        padding="size-24"
        backdrop={'static'}
        onVegaClose={handleClose}
      >
        <VegaFlex
          direction='col'
          alignItems='start'
          justifyContent='space-between'
          gap='size-8'
        >
          <VegaBox className="businessInformationModalItemList">
            <VegaForm
              ref={businessInformationForm}
            >
              {list.map((item, index) => (
                <VegaAccordion
                  accordionTitle={`Assigned Allocation ${index + 1}`}
                  expand={item.expand}
                  drawsBackground={false}
                  key={index}
                  style={{ width: '100%', padding: '0 8px 16px 0', maxHeight: '500px', overflow: 'hidden' }}
                >
                  <div slot={`content`}>
                    <div className={`prose`}>
                      <VegaFlex
                        direction='col'
                        alignItems='start'
                        justifyContent='space-between'
                        gap='size-8'
                      >
                        <VegaFlex
                          direction='row'
                          alignItems='end'
                          justifyContent='space-between'
                          gap='size-8'
                          style={{ width: '100%' }}
                        >
                          <VegaInputSelect
                            label={'Business Unit'}
                            placeholder={'Select Business Unit'}
                            size={'small'}
                            required={true}
                            source={businessUnitNameList}
                            value={item.businessUnitId}
                            onVegaChange={(e) => onChangeBusinessUnit(e.target.value, item.id, index)}
                            style={{ width: '60%', marginLeft: '5px' }}
                            vegaDropdownProps={{ searchable: false }}
                          ></VegaInputSelect>
                          <VegaButtonCircle
                            size="default"
                            disabled="false"
                            danger="false"
                            variant="icon-only"
                            icon="far fa-trash-can"
                            iconColor="text-sidebar-link-hover"
                            onVegaClick={() => removeItem(item.id, index)}
                          ></VegaButtonCircle>
                        </VegaFlex>
                        <VegaFlex
                          direction='col'
                          alignItems='start'
                          justifyContent='space-between'
                          gap='size-16'
                          style={{
                            width: '93%',
                            background: '#F1F8FB',
                            padding: '24px',
                            borderRadius: '12px'
                          }}
                        >
                          <VegaFlex
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            gap='size-16'
                            style={{ width: '100%' }}
                          >
                            <VegaInputSelect
                              label={'Project'}
                              placeholder={'Select Project'}
                              size={'small'}
                              required={true}
                              source={item.projectList}
                              value={item.projectId}
                              vegaDropdownProps={{ searchable: true }}
                              onVegaChange={(e) => onChangeProject(e.target.value, item.id, index)}
                            >
                            </VegaInputSelect>
                            <VegaInputSelect
                              label={'Market Code'}
                              placeholder={'Select Primary'}
                              size={'small'}
                              required={true}
                              value={item.marketCodeId}
                              source={marketcodeSource}
                              disabled={true}
                              vegaDropdownProps={{ searchable: false }}
                            >
                            </VegaInputSelect>
                          </VegaFlex>
                          <VegaFlex
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            gap='size-16'
                            style={{ width: '100%' }}
                          >
                            <VegaInputSelect
                              label={'Scrum Team'}
                              placeholder={'Select Scrum Team'}
                              size={'small'}
                              required={true}
                              source={getAvailableTeamOptions(item)}
                              value={item.scrumTeamId}
                              vegaDropdownProps={{ searchable: false }}
                              style={{ minHeight: '60px' }}
                              onVegaChange={(e) => onChangeTeam(e.target.value, item.id, index)}
                            >
                            </VegaInputSelect>
                            <VegaInput
                              id="allocation-input"
                              data-vega-form={`Percentage${index}`}
                              label={'Shared Percentage (%)'}
                              size={'small'}
                              required={true}
                              type="number"
                              validationRules={[PercentageRule]}
                              style={{ minHeight: '60px' }}
                              onVegaChange={(e) => onChangePercentage(e.target.value, item.id, index)}
                              value={item.allocation ? Math.round(Number(item.allocation) * 100) : ''}
                            >
                            </VegaInput>
                          </VegaFlex>
                        </VegaFlex>
                        <VegaRadio
                          checked={primarySelectionIndex === index}
                          disabled={primaryDisabled?.[index] ?? false}
                          onVegaChange={(e) => setPrimaryAllocation(index, e.target.checked)}
                          style={{ display: showPrimaryAllocation ? '' : 'none' }}
                          id="primary-allocation-radio"
                        >
                          Set as Primary allocation
                        </VegaRadio>
                      </VegaFlex>
                    </div>
                  </div>
                </VegaAccordion>
              ))}
            </VegaForm>
          </VegaBox>
          <VegaButtonLink icon="fa fa-plus" onVegaClick={createNewItem}>
            Allocation Assignment
          </VegaButtonLink>

        </VegaFlex>
        <div className='v-pt-size-4 v-pb-size-2 v--mr-size-16'>
          <VegaFlex
            direction={'row'}
            gap={'size-12'}
            margin={'size-16'}
            justifyContent={'end'}
          >
            <VegaButton
              label={'Cancel'}
              size={'small'}
              variant={'secondary'}
              onVegaClick={handleClose}
            >
            </VegaButton>
            <VegaButton
              ref={submitButtonRef}
              label={'Submit'}
              size={'small'}
              onVegaClick={openComfrimModal}
            >
            </VegaButton>
          </VegaFlex>
        </div>
      </VegaModal>

      <VegaModal
        open={comfrimModal}
        id={'business-information-confirm-modal'}
        size={'480'}
        padding="size-24"
        backdrop={'static'}
        onVegaClose={closeComfrimModal}
        // modalTitle = "All the changes will be effected once submitted."
      >
        <div slot = "modal-title" className="confirmModalTitle">
          All the changes will be effected once submitted.
        </div>
        <div className='v-pt-size-4 v-pb-size-2 v--mr-size-16'>
          <VegaFlex
            direction={'row'}
            gap={'size-12'}
            margin={'size-16'}
            justifyContent={'end'}
          >
            <VegaButton
              label={'Cancel'}
              size={'small'}
              variant={'secondary'}
              onVegaClick={closeComfrimModal}
            ></VegaButton>
            <VegaButton
              label={'Submit'}
              size={'small'}
              onVegaClick={() => {
                closeComfrimModal();
                save();
              }}
            ></VegaButton>
          </VegaFlex>
        </div>
      </VegaModal>
    </>
  );
};

export default UserProfileUpdateBusinessInformationModal;

UserProfileUpdateBusinessInformationModal.propTypes = {
  record: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  scrumTeams: PropTypes.array,
  userAllocations: PropTypes.array,
  projects: PropTypes.array,
  userId: PropTypes.string,
  loadUserInfo: PropTypes.func
};
